import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import {
    GroupTrialLaboratoriesBreadcrumbs,
    TrialGroupLaboratoriesBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupLaboratoryActions from "../../../actions/trialGroupLaboratory";
import * as TrialPersonnelActions from "../../../actions/trialPersonnel";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialSubMenu,
    TrialGroupSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialGroupLaboratoriesPage.scss";

interface TrialGroupLaboratoriesPageProps {
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    trialGroupLaboratories: Dtos.TrialGroupLaboratory[];
    loadingTrialGroupLaboratories: boolean;
    loadTrialGroupLaboratoriesSuccess: boolean;
    loadTrialGroupLaboratoriesFailure: boolean;
    loadTrialGroupLaboratoriesErrorCode: string;
    trialPersonnel: Dtos.TrialPersonnel[];
    loadingTrialPersonnel: boolean;
    loadTrialPersonnelSuccess: boolean;
    loadTrialPersonnelFailure: boolean;
    loadTrialPersonnelErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialGroupLaboratory: boolean;
    deleteTrialGroupLaboratorySuccess: boolean;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    clearTrialGroupLaboratories: TrialGroupLaboratoryActions.ITrialGroupLaboratoryClearActionCreator;
    loadTrialGroupLaboratories: TrialGroupLaboratoryActions.ITrialGroupLaboratoriesLoadByTrialGroupIdActionCreator;
    clearTrialPersonnel: TrialPersonnelActions.ITrialPersonnelClearActionCreator;
    loadTrialPersonnel: TrialPersonnelActions.ITrialPersonnelsLoadByTrialGroupIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialGroupLaboratory: TrialGroupLaboratoryActions.ITrialGroupLaboratoryDeleteActionCreator;
}

const TrialGroupLaboratoriesPage: FC<TrialGroupLaboratoriesPageProps> = ({
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupSuccess,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    trialGroupLaboratories,
    loadingTrialGroupLaboratories,
    loadTrialGroupLaboratoriesSuccess,
    loadTrialGroupLaboratoriesFailure,
    loadTrialGroupLaboratoriesErrorCode,
    trialPersonnel,
    loadingTrialPersonnel,
    //loadTrialPersonnelSuccess,
    loadTrialPersonnelFailure,
    loadTrialPersonnelErrorCode,
    permissions,
    pageSize,
    deletingTrialGroupLaboratory,
    deleteTrialGroupLaboratorySuccess,
    clearTrialGroup,
    loadTrialGroup,
    clearTrialGroupLaboratories,
    loadTrialGroupLaboratories,
    clearTrialPersonnel,
    loadTrialPersonnel,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialGroupLaboratory
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [showInActive, setShowInActive] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [trialGroupLab, setTrialGroupLab] = useState<
        Dtos.TrialGroupLaboratory | undefined
    >(undefined);

    //
    // -- Variables --
    const showTrialGroupLaboratories =
        !loadingTrialGroup && 
        loadTrialGroupSuccess &&
        !loadingTrialGroupLaboratories &&
        loadTrialGroupLaboratoriesSuccess;


    // Load TrialGroup & laboratories
    useEffect(() => {
        if (trialGroupIdNumber && !trialGroup) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoriesBreadcrumbs
                    : GroupTrialLaboratoriesBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );

            loadTrialGroup(trialGroupIdNumber);
            loadTrialGroupLaboratories(trialGroupIdNumber);
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroup,
        setBreadcrumbs,
        loadTrialGroup,
        location,
        loadTrialGroupLaboratories
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
        if (loadTrialGroupLaboratoriesFailure) {
            navigate(`/error/${loadTrialGroupLaboratoriesErrorCode}`);
        }
        if (loadTrialPersonnelFailure) {
            navigate(`/error/${loadTrialPersonnelErrorCode}`);
        }
    }, [
        loadTrialGroupFailure, 
        loadTrialGroupErrorCode, 
        loadTrialGroupLaboratoriesFailure, 
        loadTrialGroupLaboratoriesErrorCode, 
        loadTrialPersonnelFailure,
        loadTrialPersonnelErrorCode,
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialGroupIdNumber && trialGroup?.trial && trialGroup?.group) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupLaboratoriesBreadcrumbs
                    : GroupTrialLaboratoriesBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

            setDrawerItems(
                drawerItemSetter(
                    trialGroup?.groupId,
                    trialGroup?.trialId,
                    trialGroupIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup.trial?.displayName,
                    trialGroup.group?.name
                )
            );
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroup,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        location,
    ]);

    // Cleanup effect to clear the trial group on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroup();
            clearTrialGroupLaboratories();
        };
    }, [clearTrialGroup, clearTrialGroupLaboratories]);

    // Reload laboratory after delete
    useEffect(() => {
        if (
            !deletingTrialGroupLaboratory &&
            deleteTrialGroupLaboratorySuccess
        ) {
            loadTrialGroup(trialGroupIdNumber);
            loadTrialGroupLaboratories(trialGroupIdNumber);
        }
    }, [
        deletingTrialGroupLaboratory,
        deleteTrialGroupLaboratorySuccess,
        loadTrialGroup,
        loadTrialGroupLaboratories,
        trialGroupIdNumber,
    ]);

    useEffect(() => {
        if (trialGroupLab) {
            loadTrialPersonnel(trialGroupLab.id, undefined);
        }
    }, [trialGroupLab, loadTrialPersonnel]);

    const generateDeleteButton = (trialGroupLab: Dtos.TrialGroupLaboratory) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupLabUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`labDeleteButton-${trialGroupLab.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setTrialGroupLab(trialGroupLab);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const generateCreateLink = (
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber
    ) => {
        const baseRoute = location?.pathname?.split("/")[1];

        return baseRoute === "trials"
            ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/create`
            : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/create`;
    };

    const createTable = useMemo(() => {
        if (
            !showInActive &&
            trialGroupLaboratories?.filter((x) => x?.active == true)
                ?.length === 0 &&
            trialGroupLaboratories?.filter((x) => x?.active == false)
                ?.length > 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no active trial group
                            laboratories to view.
                        </span>
                    </div>
                </div>
            );
        }

        let tableData = showInActive
            ? trialGroupLaboratories
            : trialGroupLaboratories?.filter((x) => x?.active == true);

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial group laboratories to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial group laboratories to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const baseRoute =
                                    location?.pathname?.split("/")[1];

                                const url: string =
                                    baseRoute === "trials"
                                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${rowInfo.original.id}/details`
                                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/labs/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "laboratory.name",
                            Header: "Laboratory",
                            accessor: "laboratory.name",
                        },
                        {
                            id: "dateCeased",
                            Header: "Date Ceased",
                            accessor: "dateCeased",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteDepartment",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialGroupLaboratories,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        showInActive,
        trialIdNumber,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Laboratories</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        <button
                            onClick={() => {
                                setShowInActive(!showInActive);
                            }}
                            type="button"
                            className="btn btn-outline-dark mr-2"
                            style={{ minWidth: "125px" }}
                        >
                            {showInActive ? "Hide Inactive" : "Show Inactive"}
                        </button>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupLabCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={generateCreateLink(
                                    trialIdNumber,
                                    groupIdNumber,
                                    trialGroupIdNumber
                                )}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialGroupLaboratories ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialGroupLaboratories ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialGroupLaboratories,
        createTable,
        setShowInActive,
        showInActive,
        trialIdNumber,
        groupIdNumber,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupLabUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            if (!trialPersonnel || trialPersonnel?.length == 0) {
                return (
                    <Modal
                        open={openModal}
                        disableCloseOnOverlayClick={true}
                        onClose={() => {
                            setOpenModal(false);
                            setTrialGroupLab(undefined);
                            clearTrialPersonnel();
                        }}
                        size={ModalSize.Md}
                    >
                        <div className="modal-header">
                            <div
                                className="modal-title"
                                style={{ minWidth: "540px" }}
                            >
                                <h2 className="mb-0">Delete</h2>
                            </div>
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setOpenModal(false);
                                    setTrialGroupLab(undefined);
                                    clearTrialPersonnel();
                                }}
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                />
                            </button>
                        </div>
                        <div className="modal-body">
                            <span>
                                Are you sure you want to delete the laboratory "
                                <em>{trialGroupLab?.laboratory?.name}"</em> from
                                trial group{" "}
                                <strong>
                                    {trialGroup?.trial?.displayName} -{" "}
                                    {trialGroup?.group?.name}
                                </strong>
                                .
                            </span>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    deleteTrialGroupLaboratory(
                                        trialGroupLab?.id
                                    );
                                    setOpenModal(false);
                                    setTrialGroupLab(undefined);
                                    clearTrialPersonnel();
                                }}
                            >
                                Delete
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => {
                                    setOpenModal(false);
                                    setTrialGroupLab(undefined);
                                    clearTrialPersonnel();
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </Modal>
                );
            } else if (trialPersonnel && trialPersonnel.length > 0) {
                return (
                    <Modal
                        open={openModal}
                        disableCloseOnOverlayClick={true}
                        onClose={() => {
                            setOpenModal(false);
                            setTrialGroupLab(undefined);
                        }}
                        size={ModalSize.Md}
                    >
                        <div className="modal-header">
                            <div
                                className="modal-title"
                                style={{ minWidth: "540px" }}
                            >
                                <h2 className="mb-0">Restricted</h2>
                            </div>
                            <button
                                type="button"
                                className="close"
                                aria-label="Close"
                                onClick={() => {
                                    setOpenModal(false);
                                    setTrialGroupLab(undefined);
                                    clearTrialPersonnel();
                                }}
                            >
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                />
                            </button>
                        </div>
                        <div className="modal-body">
                            <span>
                                Laboratory "
                                <em>{trialGroupLab?.laboratory?.name}"</em>{" "}
                                can't be delete from trial group{" "}
                                <strong>
                                    {trialGroup?.trial?.displayName} -{" "}
                                    {trialGroup?.group?.name}
                                </strong>{" "}
                                because it contains personnel.
                            </span>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setOpenModal(false);
                                    setTrialGroupLab(undefined);
                                    clearTrialPersonnel();
                                }}
                            >
                                Okay
                            </button>
                        </div>
                    </Modal>
                );
            }
        }
        return null;
    }, [
        trialGroupLab,
        openModal,
        permissions,
        setOpenModal,
        setTrialGroupLab,
        trialGroupIdNumber,
        loadTrialGroup,
        clearTrialPersonnel,
        trialPersonnel,
        loadingTrialPersonnel
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingTrialGroup || loadingTrialGroupLaboratories}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupLaboratoriesPageProps> => {
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let trialGroupLaboratories: Dtos.TrialGroupLaboratory[] | undefined = (state.trialGroupLaboratory.formData instanceof Array)
            ? state.trialGroupLaboratory.formData
            : undefined;
    let trialPersonnel: Dtos.TrialPersonnel[] | undefined = (state.trialPersonnel.formData instanceof Array)
            ? state.trialPersonnel.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        trialGroupLaboratories: trialGroupLaboratories!,
        loadingTrialGroupLaboratories:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Pending,
        loadTrialGroupLaboratoriesSuccess:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Success,
        loadTrialGroupLaboratoriesFailure:
            state.trialGroupLaboratory.loadState &&
            state.trialGroupLaboratory.loadState.status == RequestState.Failure,
        loadTrialGroupLaboratoriesErrorCode:
            state.trialGroupLaboratory.loadState.errorCode,
        trialPersonnel: trialPersonnel!,
        loadingTrialPersonnel:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Pending,
        loadTrialPersonnelSuccess:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Success,
        loadTrialPersonnelFailure:
            state.trialPersonnel.loadState &&
            state.trialPersonnel.loadState.status == RequestState.Failure,
        loadTrialPersonnelErrorCode: state.trialPersonnel.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialGroupLaboratory:
            state.trialGroupLaboratory.deleteState &&
            state.trialGroupLaboratory.deleteState.status ==
                RequestState.Pending,
        deleteTrialGroupLaboratorySuccess:
            state.trialGroupLaboratory.deleteState &&
            state.trialGroupLaboratory.deleteState.status ==
                RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupLaboratoriesPageProps> => {
    return {
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        loadTrialGroupLaboratories: bindActionCreators(
        TrialGroupLaboratoryActions.LoadTrialGroupLaboratoriesByTrialGroupId,
            dispatch
        ),
        clearTrialGroupLaboratories: bindActionCreators(
            TrialGroupLaboratoryActions.Clear,
            dispatch
        ),
        loadTrialPersonnel: bindActionCreators(
            TrialPersonnelActions.LoadTrialPersonnelsByTrialGroupId,
            dispatch
        ),
        clearTrialPersonnel: bindActionCreators(TrialPersonnelActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialGroupLaboratory: bindActionCreators(
            TrialGroupLaboratoryActions.DeleteTrialGroupLaboratory,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupLaboratoriesPage);
